import { ArrowRight, BadgePercent, Coins, Ticket, Tag, Gamepad2 } from "lucide-react";
import { Reveal } from "../smart/Reveal";

const Eyebrow = ({ children }) => (
  <p className="text-xs font-display font-bold uppercase tracking-[0.2em] text-arcca-dark">{children}</p>
);

const Chip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-arccaink">
    <Icon size={14} strokeWidth={2.1} className="text-arcca-dark" /> {children}
  </span>
);

/* ---------- Beneficii BRD (cashback YOU BRD + campanie gaming) ---------- */
export const ArccaBrdBenefits = () => (
  <section id="beneficii-brd" data-testid="arcca-brd-benefits" className="scroll-mt-16 bg-white py-16 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="max-w-3xl">
        <Reveal><Eyebrow>Beneficii BRD</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-arccaink text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
            Și încă ceva: beneficii BRD direct pe card.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
            Pe lângă accesul la Arcca și beneficiile ISIC, BRD-ISIC îți poate aduce și avantaje prin YOU BRD și
            campaniile dedicate tinerilor.
          </p>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-6 lg:grid-cols-2 items-stretch">
        {/* Card 1 — Cashback YOU BRD (luminos) */}
        <Reveal delay={0.1}>
          <div
            data-testid="brd-benefit-cashback"
            className="group h-full flex flex-col rounded-3xl border border-slate-200 bg-white p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-arccaink/5 hover:border-arcca/40 transition-[transform,box-shadow,border-color] duration-300"
          >
            <div className="flex items-center justify-between gap-4">
              <Eyebrow>YOU BRD</Eyebrow>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-arcca/12 text-arcca-dark">
                <BadgePercent size={22} strokeWidth={2.1} />
              </span>
            </div>
            <h3 className="mt-4 font-display font-bold text-2xl text-arccaink leading-tight">Cashback în YOU BRD</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
              Descoperi oferte de cashback, vouchere sau cupoane la comercianți parteneri, direct în aplicația
              YOU BRD. Activezi ofertele atunci când este cazul, plătești cu cardul BRD și te bucuri de beneficiile
              disponibile.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Chip icon={Coins}>Cashback</Chip>
              <Chip icon={Ticket}>Voucher</Chip>
              <Chip icon={Tag}>Cupoane</Chip>
            </div>
            <p className="mt-5 text-xs leading-relaxed text-slate-400">
              Ofertele și condițiile pot varia în funcție de partener și sunt disponibile în aplicația YOU BRD.
            </p>
            <div className="mt-auto pt-6">
              <a
                href="https://www.brd.ro/persoane-fizice/online-banking/you"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="brd-cashback-cta"
                className="inline-flex items-center gap-2 rounded-full bg-arccaink px-6 py-3 text-sm font-display font-bold text-white hover:bg-black transition-colors"
              >
                Vezi cum funcționează Cashback <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </Reveal>

        {/* Card 2 — Gaming bonus BRD Visa ISIC (dark, energie de gaming) */}
        <Reveal delay={0.2}>
          <div
            data-testid="brd-benefit-gaming"
            className="group relative h-full flex flex-col overflow-hidden rounded-3xl bg-arccaink p-8 text-white hover:-translate-y-1 hover:shadow-xl hover:shadow-arccaink/20 transition-[transform,box-shadow] duration-300"
          >
            <img
              src="/assets/arcca/gaming-bg.jpg"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-45"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-arccaink via-arccaink/80 to-arccaink/25" />
            <div className="relative flex h-full flex-col">
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs font-display font-bold uppercase tracking-[0.2em] text-arcca">Campanie BRD Visa</p>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/15">
                  <Gamepad2 size={14} strokeWidth={2.1} /> Gaming
                </span>
              </div>
              <h3 className="mt-4 font-display font-bold text-2xl leading-tight">Gaming bonus cu BRD Visa ISIC</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-white/75">
                Cu BRD Visa ISIC, poți participa la campania dedicată tinerilor și poți câștiga unul dintre codurile
                Ubisoft+, conform regulamentului BRD.
              </p>
              <p className="mt-4 text-xs leading-relaxed text-white/45">
                Participarea, perioada campaniei, premiile și condițiile de acordare se aplică potrivit
                regulamentului oficial BRD.
              </p>
              <div className="mt-auto pt-6">
                <a
                  href="https://www.brd.ro/persoane-fizice/carduri-si-conturi/operatiuni-curente/card-like"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="brd-gaming-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-arcca px-6 py-3 text-sm font-display font-bold text-arccaink hover:bg-white transition-colors"
                >
                  Vezi detaliile campaniei <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
